import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import { nanoid } from 'nanoid'
import { Switch, toastStore } from 'shared/ui'
import { TitledRow } from 'shared/ui/TitledRow/TitledRow'
import modalStore from 'shared/ui/Modal/store/modalStore'
import { ModalTypeList } from 'shared/ui/Modal/store/types'
import { logger } from 'shared/lib'
import { type IHubspotSettingsDict } from 'entities/Integrations'
import { IntegrationSyncDropdown } from 'pages/settings/pages/integrations/pages/integrationSettings/ui/IntegrationSyncDropdown/IntegrationSyncDropdown'
import { InboxesTeamSelect } from 'pages/settings/pages/integrations/pages/integrationSettings/IntegrationHubspotSettings/ui/IntegrationHubspotSettingsSync/InboxesTeamSelect'
import { type IntegrationSettingsSyncStore } from 'pages/settings/pages/integrations/pages/integrationSettings/store/IntegrationSettingsSyncStore'
import {
  getContactCreation,
  getContactOwnershipSync,
  getPropertySync,
} from 'pages/settings/pages/integrations/pages/integrationSettings/lib'
import styles from './styles.module.scss'

type IIntegrationHubspotSettingsSyncProps = {
  store: IntegrationSettingsSyncStore<IHubspotSettingsDict>
}

export const IntegrationHubspotSettingsSync: React.FC<IIntegrationHubspotSettingsSyncProps> =
  observer(({ store }) => {
    const {
      isTwoWaySync,
      setSyncType,
      syncType,
      setExpandedCheckbox,
      getCheckbox,
      selectItems,
      integrationName,
    } = store

    const [pendingModalId, setPendingModalId] = useState<string | null>(null)

    const CHECKBOXES = [
      getContactOwnershipSync(integrationName),
      getPropertySync('update_contacts', integrationName),
      getContactCreation('create_non_existing_contacts', integrationName),
    ]

    const handleUpdateOwnerToggle = async (value: boolean) => {
      const currentValue = getCheckbox('update_owner')

      if (value && !currentValue && !pendingModalId) {
        const modalId = nanoid()
        setPendingModalId(modalId)

        modalStore.addModal({
          id: modalId,
          type: ModalTypeList.WARNING,
          title: 'Contact owners may be overwritten',
          desc: 'Enabling this option allows Salesmsg to update the contact owner in HubSpot. This may replace the current owner.',
          primaryAction: {
            text: 'Enable anyway',
            onAction: async () => {
              modalStore.removeModal(modalId)
              setPendingModalId(null)
              try {
                await setExpandedCheckbox('update_owner')(true)
                toastStore.add({
                  type: 'info',
                  title: 'Contact ownership sync enabled',
                })
              } catch (error) {
                logger.error(error)

                throw error
              }
            },
          },
          secondaryAction: {
            text: 'Cancel',
            onAction: () => {
              modalStore.removeModal(modalId)
              setPendingModalId(null)
            },
          },
          onClose: () => {
            modalStore.removeModal(modalId)
            setPendingModalId(null)
          },
        })
      } else if (!value) {
        if (pendingModalId) {
          modalStore.removeModal(pendingModalId)
          setPendingModalId(null)
        }
        setExpandedCheckbox('update_owner')(false)
      }
    }

    const getToggleHandler = (field: string) => {
      if (field === 'update_owner') {
        return handleUpdateOwnerToggle
      }
      return setExpandedCheckbox(field)
    }

    return (
      <div className={styles.root}>
        <IntegrationSyncDropdown
          integrationIcon={'integrationHubspot'}
          isTwoWaySync={isTwoWaySync}
          iconHeight={28.5}
          syncType={syncType}
          setSyncType={setSyncType}
          selectItems={selectItems}
        />
        {isTwoWaySync &&
          CHECKBOXES.map(({ field, title, label }) => (
            <TitledRow key={field} title={title} label={label} verticalAlign={'start'}>
              <Switch
                size='middle'
                value={getCheckbox(field)}
                onChange={getToggleHandler(field)}
                disabled={field === 'update_owner' && !!pendingModalId}
              />
            </TitledRow>
          ))}
        <TitledRow
          title={'Fallback inbox'}
          label={'If HubSpot contact owner is not recognized, send from this inbox'}
          align={'normal'}
        >
          <InboxesTeamSelect />
        </TitledRow>
      </div>
    )
  })
